import type { Dict } from "../core.js";
import type { mailProviders as vi } from "../vi/mailProviders.js";

export const mailProviders: Dict<typeof vi> = {
  configureProviders: "Configure email providers",
  title: "Email provider accounts",
  subtitle:
    "The hub reads Resend / Elastic Email / Amazon SES accounts with each credential set's MANAGEMENT key (the key stays in the hub, never goes to apps) — every 10 minutes, or when you press « Read again ». Every number carries its source.",
  back: "← Mail",
  backList: "← All providers",
  loading: "Loading…",
  loadFailed: "Could not read the provider accounts.",
  empty: "No Resend / Elastic Email / Amazon SES credential set yet — add one on the Integrations page.",
  open: "Details",
  refresh: "Read again",
  refreshing: "Reading…",
  checkedAt: "Read at {at}",
  neverChecked: "never read",
  "col.set": "Credential set",
  "col.read": "Account read",
  "col.quota": "Quota",
  "col.account": "Account",
  "col.webhook": "Delivery events",
  "col.checked": "Read at",
  "env.live": "Live",
  "env.sandbox": "Sandbox",
  "provider.resend": "Resend",
  "provider.elasticmail": "Elastic Email",
  "provider.ses": "Amazon SES",
  "read.ok": "Readable",
  "read.no_key": "No management key",
  "read.error": "Error",
  "read.decrypt_failed": "Hub cannot open the set",
  "read.never": "Not read yet",
  "readHint.no_key":
    "This set has no MANAGEMENT key (a « hub only » field of the credential set) — the hub can only show manually entered figures. Enter the key on the Integrations page.",
  "readHint.decrypt_failed": "The hub cannot decrypt this credential set (did the hub sealing key change?) — check the API log.",
  "readHint.never": "Not read yet — press « Read again ».",
  "error.auth": "The provider rejected the management key",
  "error.permission": "The management key lacks permissions",
  "error.not_found": "Not found",
  "error.conflict": "Conflict",
  "error.invalid_request": "Invalid request",
  "error.rate_limited": "Rate limited",
  "error.unavailable": "Could not reach the provider",
  "error.invalid_response": "Unexpected provider response",
  "source.api": "API",
  "source.header": "header",
  "source.manual": "manual",
  "source.none": "unknown",
  "metric.messages": "mails",
  "metric.recipients": "recipients",
  "metric.requests": "requests",
  "metric.domains": "domains",
  "metric.contacts": "contacts",
  "window.rolling_24h": "rolling 24h",
  "window.day": "day",
  "window.month": "month",
  "window.second": "second",
  "window.total": "total",
  quotaLine: "{metric} / {window}",
  quotaUsed: "{used} / {limit}",
  unlimited: "no cap",
  resetsAt: "resets {at}",
  noQuota: "The provider has no quota API — see the manually entered plan / credits.",
  "tab.overview": "Overview",
  "tab.domains": "Domains",
  "overview.plan": "Plan",
  "overview.quotas": "Quotas",
  "overview.rateLimit": "Rate",
  "overview.rateLimitValue": "{limit} calls / {sec} s",
  "overview.credits": "Credits",
  "overview.sandbox": "Sandbox",
  "overview.sending": "Sending enabled",
  "overview.enforcement": "Enforcement status",
  "overview.stats": "This month",
  yes: "yes",
  no: "no",
  unknownValue: "—",
  "enforcement.healthy": "healthy",
  "enforcement.probation": "under review (PROBATION)",
  "enforcement.shutdown": "sending blocked (SHUTDOWN)",
  "stat.recipients": "recipients",
  "stat.emails": "mails",
  "stat.delivered": "delivered",
  "stat.bounced": "bounced",
  "stat.complaints": "complaints",
  "stat.notDelivered": "not delivered",
  "stat.inProgress": "in progress",
  "stat.unsubscribed": "unsubscribed",
  "webhook.absent": "Not connected",
  "webhook.pending": "Awaiting confirmation",
  "webhook.active": "Receiving",
  "webhook.failed": "Failed",
  "webhook.lastEvent": "last event {at}",
  "tab.events": "Webhook / Events",
  "events.status": "Status",
  "events.url": "Hub receiving URL",
  "events.connectedAt": "Connected at",
  "events.lastEventAt": "Last event",
  "events.noEventYet": "no event received yet",
  "events.pendingHint.resend": "Connected — waiting for the first event.",
  "events.pendingHint.elasticmail": "Connected — waiting for the first event.",
  "events.pendingHint.ses": "Waiting for SNS to send the subscription confirmation — the hub confirms it after checking the signature.",
  "events.what.resend":
    "« Connect webhook » points Resend at the hub URL for delivery events (delivered, bounced, complained, delayed…). The signing secret issued by Resend is stored sealed in the hub only. Press again to check, repair or rotate.",
  "events.what.elasticmail":
    "« Connect webhook » points Elastic Email at the hub URL (HMAC signing on) for delivery events. The signing secret is stored sealed in the hub only. Press again to check, repair or rotate.",
  "events.what.ses":
    "« Connect webhook » sets up AWS: an SNS topic (signature v2), an HTTPS subscription to the hub, a Configuration Set and an Event Destination; then writes `configurationSetName` into the credential set and pushes it to apps — only from then on do mails sent through SES produce delivery events.",
  "events.readOnly": "You have read-only access. Connecting the webhook requires `hq.integrations.write`.",
  "events.ensure": "Connect webhook",
  "events.reEnsure": "Check & reconnect webhook",
  "events.counts24h": "Last 24 hours",
  "events.countsEmpty": "No events in the last 24 hours.",
  "events.recent": "Recent events",
  "events.empty": "The hub has not received any delivery event for this set yet.",
  "events.col.at": "At",
  "events.col.event": "Event",
  "events.col.mail": "Mail",
  "events.col.recipient": "Recipient domain",
  "events.col.diagnostic": "Diagnostic",
  "events.confirmTitle": "Connect the delivery-event webhook",
  "events.confirmEffect.resend": "The hub creates or repairs the webhook on this Resend account with the management key; a new signing secret (if any) is stored sealed in the hub only.",
  "events.confirmEffect.elasticmail": "The hub creates or repairs the webhook on this Elastic Email account with the management key; a new signing secret (if any) is stored sealed in the hub only.",
  "events.confirmEffect.ses":
    "The hub creates / repairs the SNS topic, HTTPS subscription, Configuration Set and Event Destination on AWS; writes `configurationSetName` into the credential set ⇒ a new version + a config push to the apps using this set.",
  "events.confirm": "Connect webhook",
  "events.ensuring": "Connecting…",
  "delivery.accepted": "accepted",
  "delivery.sent": "sent",
  "delivery.delayed": "delayed",
  "delivery.delivered": "delivered",
  "delivery.bounced": "bounced",
  "delivery.rejected": "rejected",
  "delivery.complained": "complaint",
  "bounce.hard": "hard",
  "bounce.soft": "soft",
  "bounce.unknown": "unknown",
  "attribution.request_tag": "matched by send-intent tag",
  "attribution.mail_tag": "matched by mail tag",
  "attribution.provider_message_id": "matched by provider message id",
  "attribution.binding": "matched by the app using the set",
  "attribution.none": "not matched to an app",
  "tab.keys": "Keys",
  "tab.suppressions": "Suppressions",
  "confirm.cancel": "Cancel",
  "confirm.failed": "The operation did not succeed.",
  "confirm.audit": "The operation is recorded in the audit log.",
  "confirm.typeSet": "Type the set name « {name} » to confirm:",
  "confirm.typeDomain": "Type the domain « {name} » to confirm:",
  "confirm.typeKey": "Type the key name « {name} » to confirm:",
  "confirm.typeEmail": "Type the address « {email} » to confirm:",
  "domains.add": "Add domain",
  "domains.addTitle": "Add a sending domain",
  "domains.addName": "Domain name (e.g. mail.bube.vn)",
  "domains.addEffect": "The hub creates the domain on the provider account with the management key. Then add the DNS records shown at your DNS host and press « Check DNS again ».",
  "domains.adding": "Adding…",
  "domains.verify": "Check DNS again",
  "domains.verifying": "Checking…",
  "domains.remove": "Remove domain",
  "domains.removeTitle": "Remove domain {name}",
  "domains.removeEffect": "The provider removes the domain and every verification record — mail sent from this domain is rejected immediately.",
  "domains.removing": "Removing…",
  "keys.loadFailed": "Could not read the key list.",
  "keys.readError": "The provider did not return the key list:",
  "keys.unsupportedProvider": "This provider does not manage keys through an API ({reason}).",
  "keys.empty": "The account has no API key yet.",
  "keys.readOnly": "You have read-only access. Creating / removing keys requires `hq.integrations.write`.",
  "keys.col.name": "Name",
  "keys.col.permission": "Permission",
  "keys.col.domain": "Domain",
  "keys.col.created": "Created",
  "keys.col.lastUsed": "Last used",
  "keys.byHub": "by hub",
  "keys.inUse": "in use",
  "keys.inUseHint": "An app sends with this key (a route sends from its domain, or it is the set's API key) — create a new key for the domain first, then remove this one.",
  "keys.never": "never",
  "keys.domain": "Domain (verified)",
  "keys.create": "Create new sending key",
  "keys.createTitle": "Create a sending key for a domain",
  "keys.createEffect": "The hub creates a SEND-ONLY key restricted to the chosen domain and stores it per (set, domain). Every app with a route sending from this domain switches to the new key on the next push; the set's API key is untouched. The key is never shown. The domain's old key stays at the provider until you remove it.",
  "keys.creating": "Creating…",
  "keys.created": "Key {name} created for domain {domain}.",
  "keys.createdPushed": "Config pushed to the apps sending from this domain.",
  "keys.createdIdle": "No app sends from this domain yet — the key is used once a route sends from it.",
  "keys.perDomain": "Sending keys per domain",
  "keys.perDomainHint": "A set is one provider account and can be shared by many apps. An app's route sends with the key of the domain in its sender address; a domain without its own key uses the set's API key.",
  "keys.perDomainEmpty": "No per-domain sending key yet — every route sends with the set's API key.",
  "keys.setApiKey": "is the set's API key",
  "keys.noVerifiedDomain": "No verified domain yet — add and verify a domain in the « Domains » tab before creating a sending key.",
  "keys.remove": "Remove",
  "keys.removeTitle": "Remove key {name}",
  "keys.removeEffect": "The provider revokes the key immediately — anything still using it can no longer send.",
  "keys.removing": "Removing…",
  "supp.loadFailed": "Could not read the suppression list.",
  "supp.readError": "The provider did not return the suppression list:",
  "supp.unsupportedProvider": "This provider has no suppression-list API ({reason}).",
  "supp.hint": "Addresses the provider suppresses (hard bounce, complaint, added manually…) — mail to them is dropped.",
  "supp.fetchedAt": "Read from the provider at {at} (the hub keeps it 5 minutes, never stores it).",
  "supp.empty": "No address is suppressed.",
  "supp.col.email": "Address",
  "supp.col.origin": "Reason",
  "supp.col.at": "Suppressed at",
  "supp.col.detail": "Detail",
  "supp.origin.bounce": "bounce",
  "supp.origin.complaint": "complaint",
  "supp.origin.manual": "manual",
  "supp.origin.unsubscribe": "unsubscribed",
  "supp.origin.unknown": "other",
  "supp.next": "Next page",
  "supp.first": "Back to first page",
  "supp.remove": "Unsuppress",
  "supp.removeTitle": "Remove {email} from the suppression list",
  "supp.removeEffect": "The provider will send to this address again. Only remove it when the address is fixed / the recipient agreed — resending to a bad address hurts sender reputation.",
  "supp.removing": "Removing…",
  "domains.empty": "The account has no domain yet.",
  "domains.notRead": "Domains not read (needs the management key).",
  "domains.sending": "sending",
  "domains.notSending": "not sending yet",
  "domains.col.purpose": "Purpose",
  "domains.col.type": "Type",
  "domains.col.name": "Name",
  "domains.col.value": "Value",
  "domains.col.status": "Status",
  "domains.valueHidden": "(see the provider dashboard)",
  "record.verified": "verified",
  "record.pending": "pending",
  "record.failed": "failed",
  "record.unknown": "unknown",
  "dmarc.title": "DMARC suggestion for {domain}",
  "dmarc.hint": "The domain is sending but no verified DMARC record was seen. Start with p=none (monitor only), then tighten.",
  "dmarc.copy": "Copy",
  "dmarc.copied": "Copied",
};
